/**
 * Utilitários para gerenciamento de configurações do sistema
 * Centraliza o acesso às configurações em toda a aplicação
 */
import React from 'react';
import api from './axiosConfig';

// ⚠️ ATENÇÃO: Configurações padrão do sistema (apenas para FALLBACK de emergência)
// Estes valores NUNCA devem ser usados em produção!
// Configure os valores reais através da tela de Configurações do Sistema
const configuracoesDefault = {
  // Valores financeiros zerados - DEVEM ser configurados no sistema
  valorConsulta: 0.00,
  valorRetorno: 0.00,
  valorExame: 0.00,
  // Dados da clínica - substitua pelos dados reais
  nomeClinica: 'Clínica Não Configurada',
  emailClinica: 'configure@sistema.com',
  telefoneClinica: '(00) 00000-0000',
  enderecoClinica: 'Endereço não configurado',
  // Preferências padrão
  notificacaoEstoque: true,
  notificacaoConsultas: true,
  notificacaoVencimento: true,
  backupAutomatico: true
};

/**
 * Busca configurações financeiras do banco de dados
 */
export const buscarConfiguracoesFinanceirasDoBanco = async (forceRefresh = false) => {
  try {
    const token = localStorage.getItem('token');
    console.log('🔑 Token disponível:', !!token);
    console.log('🔄 Force refresh:', forceRefresh);
    
    if (!token) {
      console.warn('⚠️ Sem token - não é possível buscar do banco');
      throw new Error('Token de autenticação necessário para buscar configurações do banco');
    }

    // Se forceRefresh=true, pular cache
    if (forceRefresh) {
      console.log('🚀 Forçando refresh - ignorando cache');
    }

    console.log('🌐 Fazendo requisição para /configuracoes-financeiras...');
    const response = await api.get('/configuracoes-financeiras');
    console.log('📡 Resposta completa da API:', response);
    console.log('📋 Data da resposta:', response.data);
    
    if (response.data.success && response.data.data) {
      console.log('✅ Dados válidos recebidos:', response.data.data);
      
      if (response.data.data.valores) {
        console.log('💰 Valores encontrados:', response.data.data.valores);
        return response.data.data.valores;
      } else {
        console.log('⚠️ Propriedade valores não encontrada, usando configuracoes array');
        // Se não tem valores, tentar usar o array de configurações
        const configs = response.data.data.configuracoes;
        if (Array.isArray(configs)) {
          const valores = {};
          configs.forEach(config => {
            valores[config.chave] = parseFloat(config.valor);
          });
          console.log('🔄 Valores convertidos do array:', valores);
          return valores;
        }
      }
    }
    
    throw new Error('Dados inválidos recebidos do servidor');
  } catch (error) {
    console.error('❌ Erro ao buscar configurações financeiras do banco:', error);
    console.error('❌ Detalhes do erro:', error.response?.data || error.message);
    
    // Re-throw o erro para que a tela de configurações saiba que falhou
    throw error;
  }
};

/**
 * Salva configurações financeiras no banco de dados
 */
export const salvarConfiguracoesFinanceirasBanco = async (valores) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Token de autenticação necessário');
    }

    const configuracoes = [
      { chave: 'valor_consulta', valor: valores.valorConsulta, descricao: 'Valor padrão para consultas normais' },
      { chave: 'valor_retorno', valor: valores.valorRetorno, descricao: 'Valor padrão para consultas de retorno' },
      { chave: 'valor_exame', valor: valores.valorExame, descricao: 'Valor padrão para exames' }
    ];

    const response = await api.post('/configuracoes-financeiras/multiplas', { configuracoes });
    
    if (response.data.success) {
      console.log('✅ Configurações salvas no banco:', response.data);
      
      // Limpar cache para forçar reload na próxima busca
      cacheConfiguracoesFinanceiras = null;
      ultimaAtualizacaoCache = null;
      
      return true;
    }
    
    throw new Error('Falha ao salvar no banco');
  } catch (error) {
    console.error('❌ Erro ao salvar configurações no banco:', error);
    return false;
  }
};

/**
 * Carrega as configurações do sistema
 * Primeiro tenta do localStorage, senão usa as padrão
 */
export const carregarConfiguracoes = () => {
  try {
    const configSalvas = localStorage.getItem('configuracoes_sistema');
    if (configSalvas) {
      const config = JSON.parse(configSalvas);
      // Mescla com as configurações padrão para garantir que todas as propriedades existam
      return { ...configuracoesDefault, ...config };
    }
    return configuracoesDefault;
  } catch (error) {
    console.error('Erro ao carregar configurações:', error);
    return configuracoesDefault;
  }
};

/**
 * Salva as configurações no localStorage
 */
export const salvarConfiguracoes = (novasConfiguracoes) => {
  try {
    localStorage.setItem('configuracoes_sistema', JSON.stringify(novasConfiguracoes));
    return true;
  } catch (error) {
    console.error('Erro ao salvar configurações:', error);
    return false;
  }
};

/**
 * Obtém uma configuração específica
 */
export const obterConfiguracao = (chave, valorPadrao = null) => {
  const configuracoes = carregarConfiguracoes();
  return configuracoes[chave] !== undefined ? configuracoes[chave] : valorPadrao;
};

/**
 * Cache das configurações financeiras para evitar múltiplas requisições
 */
let cacheConfiguracoesFinanceiras = null;
let ultimaAtualizacaoCache = null;

/**
 * Obtém configurações financeiras (com cache de 5 minutos)
 */
const obterConfiguracoesFinanceiras = async () => {
  const agora = new Date();
  
  // Se tem cache e não expirou (5 minutos), usar cache
  if (cacheConfiguracoesFinanceiras && ultimaAtualizacaoCache && 
      (agora - ultimaAtualizacaoCache) < 5 * 60 * 1000) {
    return cacheConfiguracoesFinanceiras;
  }
  
  // Buscar do banco
  const configsBanco = await buscarConfiguracoesFinanceirasDoBanco();
  
  // Atualizar cache
  cacheConfiguracoesFinanceiras = configsBanco;
  ultimaAtualizacaoCache = agora;
  
  return configsBanco;
};

/**
 * Obtém o valor atual da consulta (do banco)
 */
export const obterValorConsulta = async () => {
  try {
    const configs = await obterConfiguracoesFinanceiras();
    const valor = configs.valor_consulta !== undefined ? configs.valor_consulta : 0.00;
    
    if (valor === 0.00) {
      console.warn('⚠️ AVISO: Valor de consulta não configurado (R$ 0,00). Configure nas Configurações do Sistema.');
    }
    
    return valor;
  } catch (error) {
    console.error('Erro ao obter valor consulta:', error);
    console.warn('⚠️ Usando valor padrão R$ 0,00 - Configure os valores no sistema!');
    return 0.00;
  }
};

/**
 * Obtém o valor atual do retorno (do banco)
 */
export const obterValorRetorno = async () => {
  try {
    const configs = await obterConfiguracoesFinanceiras();
    const valor = configs.valor_retorno !== undefined ? configs.valor_retorno : 0.00;
    
    if (valor === 0.00) {
      console.warn('⚠️ AVISO: Valor de retorno não configurado (R$ 0,00). Configure nas Configurações do Sistema.');
    }
    
    return valor;
  } catch (error) {
    console.error('Erro ao obter valor retorno:', error);
    console.warn('⚠️ Usando valor padrão R$ 0,00 - Configure os valores no sistema!');
    return 0.00;
  }
};

/**
 * Obtém o valor atual do exame (do banco)
 */
export const obterValorExame = async () => {
  try {
    const configs = await obterConfiguracoesFinanceiras();
    const valor = configs.valor_exame !== undefined ? configs.valor_exame : 0.00;
    
    if (valor === 0.00) {
      console.warn('⚠️ AVISO: Valor de exame não configurado (R$ 0,00). Configure nas Configurações do Sistema.');
    }
    
    return valor;
  } catch (error) {
    console.error('Erro ao obter valor exame:', error);
    console.warn('⚠️ Usando valor padrão R$ 0,00 - Configure os valores no sistema!');
    return 0.00;
  }
};

/**
 * Calcula o faturamento de consultas baseado na quantidade e tipo
 */
export const calcularFaturamentoConsultas = (consultas = []) => {
  const valorConsulta = obterValorConsulta();
  const valorRetorno = obterValorRetorno();
  
  return consultas.reduce((total, consulta) => {
    if (consulta.tipo === 'retorno') {
      return total + valorRetorno;
    }
    return total + valorConsulta;
  }, 0);
};

/**
 * Calcula o faturamento por médico
 */
export const calcularFaturamentoPorMedico = (medicos = [], consultas = []) => {
  const valorConsulta = obterValorConsulta();
  const valorRetorno = obterValorRetorno();
  
  return medicos.map(medico => {
    const consultasMedico = consultas.filter(c => c.medico === medico.nome);
    const faturamento = consultasMedico.reduce((total, consulta) => {
      if (consulta.tipo === 'retorno') {
        return total + valorRetorno;
      }
      return total + valorConsulta;
    }, 0);
    
    return {
      ...medico,
      consultas: consultasMedico.length,
      faturamento
    };
  });
};

/**
 * Atualiza os dados financeiros baseados nas configurações atuais
 */
export const atualizarDadosFinanceiros = (consultas = [], farmacia = 0, exames = []) => {
  const faturamentoConsultas = calcularFaturamentoConsultas(consultas);
  const faturamentoExames = exames.reduce((total, exame) => {
    return total + (parseFloat(exame.valor) || obterValorExame());
  }, 0);
  
  const total = faturamentoConsultas + farmacia + faturamentoExames;
  
  return [
    {
      tipo: 'Consultas',
      valor: faturamentoConsultas,
      porcentagem: total > 0 ? ((faturamentoConsultas / total) * 100) : 0
    },
    {
      tipo: 'Farmácia',
      valor: farmacia,
      porcentagem: total > 0 ? ((farmacia / total) * 100) : 0
    },
    {
      tipo: 'Exames',
      valor: faturamentoExames,
      porcentagem: total > 0 ? ((faturamentoExames / total) * 100) : 0
    }
  ];
};

/**
 * Formata valor em reais
 */
export const formatarMoeda = (valor) => {
  // Aceita zero e valores válidos
  const valorNumerico = isNaN(parseFloat(valor)) ? 0 : parseFloat(valor);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(valorNumerico);
};

/**
 * Event listener para mudanças nas configurações
 * Permite que componentes sejam notificados quando as configurações mudam
 */
class ConfiguracoesEventManager {
  constructor() {
    this.listeners = [];
  }

  adicionarListener(callback) {
    this.listeners.push(callback);
  }

  removerListener(callback) {
    this.listeners = this.listeners.filter(l => l !== callback);
  }

  notificarMudanca(configuracoes) {
    this.listeners.forEach(callback => {
      try {
        callback(configuracoes);
      } catch (error) {
        console.error('Erro ao notificar mudança de configuração:', error);
      }
    });
  }
}

export const configuracoesEventManager = new ConfiguracoesEventManager();

/**
 * Hook personalizado para usar configurações em componentes React
 */
export const useConfiguracoes = () => {
  const [configuracoes, setConfiguracoes] = React.useState(carregarConfiguracoes());
  const [valoresFinanceiros, setValoresFinanceiros] = React.useState({
    valorConsulta: 0.00,
    valorRetorno: 0.00,
    valorExame: 0.00
  });

  React.useEffect(() => {
    const handleConfigChange = (novasConfiguracoes) => {
      setConfiguracoes(novasConfiguracoes);
    };

    configuracoesEventManager.adicionarListener(handleConfigChange);

    // Carregar valores financeiros do banco
    const carregarValoresFinanceiros = async () => {
      try {
        const configs = await buscarConfiguracoesFinanceirasDoBanco();
        setValoresFinanceiros({
          valorConsulta: configs.valor_consulta !== undefined ? configs.valor_consulta : 0.00,
          valorRetorno: configs.valor_retorno !== undefined ? configs.valor_retorno : 0.00,
          valorExame: configs.valor_exame !== undefined ? configs.valor_exame : 0.00
        });
      } catch (error) {
        console.error('Erro ao carregar valores financeiros:', error);
      }
    };

    carregarValoresFinanceiros();

    return () => {
      configuracoesEventManager.removerListener(handleConfigChange);
    };
  }, []);

  const atualizarConfiguracoes = async (novasConfiguracoes) => {
    // Se tem valores financeiros, salvar no banco
    if (novasConfiguracoes.valorConsulta !== undefined || 
        novasConfiguracoes.valorRetorno !== undefined || 
        novasConfiguracoes.valorExame !== undefined) {
      
      const sucesso = await salvarConfiguracoesFinanceirasBanco(novasConfiguracoes);
      if (sucesso) {
        // Atualizar o estado local
        setValoresFinanceiros(prev => ({
          ...prev,
          valorConsulta: novasConfiguracoes.valorConsulta || prev.valorConsulta,
          valorRetorno: novasConfiguracoes.valorRetorno || prev.valorRetorno,
          valorExame: novasConfiguracoes.valorExame || prev.valorExame
        }));
        
        // Limpar cache para forçar reload
        cacheConfiguracoesFinanceiras = null;
      }
    }
    
    // Salvar outras configurações no localStorage
    if (salvarConfiguracoes(novasConfiguracoes)) {
      setConfiguracoes(novasConfiguracoes);
      configuracoesEventManager.notificarMudanca(novasConfiguracoes);
      return true;
    }
    return false;
  };

  return {
    configuracoes,
    atualizarConfiguracoes,
    valorConsulta: valoresFinanceiros.valorConsulta,
    valorRetorno: valoresFinanceiros.valorRetorno,
    valorExame: valoresFinanceiros.valorExame
  };
};

const configuracaoUtils = {
  carregarConfiguracoes,
  salvarConfiguracoes,
  obterConfiguracao,
  obterValorConsulta,
  obterValorRetorno,
  obterValorExame,
  calcularFaturamentoConsultas,
  calcularFaturamentoPorMedico,
  atualizarDadosFinanceiros,
  formatarMoeda,
  configuracoesEventManager
};

export default configuracaoUtils;